/**
 * fileTreeStore — single source of truth for the workspace file tree.
 *
 * Holds the node tree, expansion state, loading state, git-status overlay,
 * and a "recently flashed" set used to briefly highlight nodes that just
 * appeared (e.g. an AI agent created a file in the terminal).
 *
 * All identifiers are the FileNode `id` field, which is the project-relative
 * path with forward slashes (e.g. `"src/lib/foo.ts"`). Root-level nodes have
 * a parent id of `""` (empty string).
 *
 * The watcher subscription in FilesTab applies per-path events here via
 * `insertNode` / `removeNode`, which is what makes the AI-creates-file →
 * tree-shows-file UX work without losing the user's expansion state.
 */
import { create } from 'zustand';
import type { FileNode, GitFileStatus } from '@/services/backend/types';

export interface FileNodeWithContent extends FileNode {
  content?: string;
  children?: FileNodeWithContent[];
}

const FLASH_DURATION_MS = 1500;

interface FileTreeState {
  // Identity
  projectId: string | null;
  setProjectId: (id: string | null) => void;

  // Tree
  tree: FileNodeWithContent[];
  setTree: (tree: FileNodeWithContent[]) => void;
  setChildren: (parentId: string, children: FileNodeWithContent[]) => void;

  // Expansion
  expandedIds: Set<string>;
  toggleExpanded: (id: string) => void;
  setExpanded: (id: string, expanded: boolean) => void;

  // Loading
  loadingIds: Set<string>;
  setLoading: (id: string, loading: boolean) => void;

  // Git
  gitStatus: Map<string, GitFileStatus['status']>;
  setGitStatus: (status: Map<string, GitFileStatus['status']>) => void;

  /**
   * Insert `node` as a child of `parentId` (or at the root if `parentId` is
   * the empty string). Idempotent — duplicates are ignored. If the parent
   * exists but hasn't had its children loaded yet (collapsed folder), the
   * insert is skipped — the lazy load will pick the new node up later.
   * Returns `true` if anything changed.
   */
  insertNode: (parentId: string, node: FileNodeWithContent) => boolean;
  /** Remove a node from anywhere in the tree. Returns `true` if found. */
  removeNode: (id: string) => boolean;
  /**
   * Merge a full-tree crawl result into the existing tree. Used to populate
   * the tree cache after the initial shallow load. Per-folder rule:
   * - If the existing folder hasn't been loaded (`children === undefined`),
   *   adopt the crawl's children.
   * - If the existing folder is already loaded (lazy-load or watcher
   *   inserts), keep the existing children but recurse to fill any
   *   still-undefined grandchildren.
   * - Nodes present in the crawl but not in the existing tree are added
   *   (e.g. files created between initial load and crawl completion).
   * - Existing nodes not in the crawl are preserved (watcher inserts that
   *   landed during the crawl).
   */
  mergeFullCrawl: (crawl: FileNodeWithContent[]) => void;

  // Flash
  flashIds: Set<string>;
  flash: (id: string) => void;

  reset: () => void;
}

// ───────────────────────── Tree helpers (immutable) ─────────────────────────

function sortNodes(nodes: FileNodeWithContent[]): FileNodeWithContent[] {
  return [...nodes].sort((a, b) => {
    if (a.type === 'folder' && b.type === 'file') return -1;
    if (a.type === 'file' && b.type === 'folder') return 1;
    return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
  });
}

function applySetChildren(
  tree: FileNodeWithContent[],
  parentId: string,
  children: FileNodeWithContent[],
): FileNodeWithContent[] {
  return tree.map((node) => {
    if (node.id === parentId) {
      return { ...node, children };
    }
    if (node.children) {
      return { ...node, children: applySetChildren(node.children, parentId, children) };
    }
    return node;
  });
}

function applyInsert(
  tree: FileNodeWithContent[],
  parentId: string,
  newNode: FileNodeWithContent,
): { tree: FileNodeWithContent[]; inserted: boolean } {
  if (parentId === '') {
    if (tree.some((n) => n.id === newNode.id)) return { tree, inserted: false };
    return { tree: sortNodes([...tree, newNode]), inserted: true };
  }

  let inserted = false;
  const next = tree.map((node) => {
    if (node.id === parentId) {
      // Don't create children out of thin air — a collapsed folder might
      // reasonably have `undefined` children. The next lazy-load will fetch
      // the up-to-date list including this new node.
      if (!node.children) return node;
      if (node.children.some((c) => c.id === newNode.id)) return node;
      inserted = true;
      return { ...node, children: sortNodes([...node.children, newNode]) };
    }
    if (node.children) {
      const result = applyInsert(node.children, parentId, newNode);
      if (result.inserted) {
        inserted = true;
        return { ...node, children: result.tree };
      }
    }
    return node;
  });

  return { tree: inserted ? next : tree, inserted };
}

/**
 * Union-merge two tree slices. See `mergeFullCrawl` doc on the store for the
 * per-folder semantics.
 */
function applyMergeCrawl(
  existing: FileNodeWithContent[],
  crawl: FileNodeWithContent[],
): FileNodeWithContent[] {
  const existingById = new Map(existing.map((n) => [n.id, n]));
  const crawlById = new Map(crawl.map((n) => [n.id, n]));
  const allIds = new Set<string>();
  for (const id of existingById.keys()) allIds.add(id);
  for (const id of crawlById.keys()) allIds.add(id);

  const merged: FileNodeWithContent[] = [];
  for (const id of allIds) {
    const e = existingById.get(id);
    const c = crawlById.get(id);

    if (e && !c) {
      merged.push(e);
      continue;
    }
    if (!e && c) {
      merged.push(c);
      continue;
    }
    if (!e || !c) continue; // unreachable — Set comes from union of both maps

    // Both present.
    if (e.type !== 'folder') {
      // File — keep existing (e.g. it already has loaded `content`).
      merged.push(e);
      continue;
    }

    if (e.children === undefined) {
      // Folder never loaded — adopt the crawl's children wholesale.
      merged.push({
        ...e,
        children: c.children,
        hasChildren: !!c.children?.length || !!c.hasChildren,
      });
      continue;
    }

    if (c.children) {
      // Folder loaded; recurse to fill undefined grandchildren.
      merged.push({
        ...e,
        children: applyMergeCrawl(e.children, c.children),
      });
    } else {
      merged.push(e);
    }
  }

  return sortNodes(merged);
}

function applyRemove(
  tree: FileNodeWithContent[],
  id: string,
): { tree: FileNodeWithContent[]; removed: boolean } {
  if (tree.some((n) => n.id === id)) {
    return { tree: tree.filter((n) => n.id !== id), removed: true };
  }
  let removed = false;
  const next = tree.map((node) => {
    if (node.children) {
      const result = applyRemove(node.children, id);
      if (result.removed) {
        removed = true;
        return { ...node, children: result.tree };
      }
    }
    return node;
  });
  return { tree: removed ? next : tree, removed };
}

// ─────────────────────────────── Store ────────────────────────────────────

// Module-level so flash timers persist across re-renders. Cleared on reset().
const flashTimers: Map<string, ReturnType<typeof setTimeout>> = new Map();

export const useFileTreeStore = create<FileTreeState>((set, get) => ({
  projectId: null,
  tree: [],
  expandedIds: new Set(),
  loadingIds: new Set(),
  gitStatus: new Map(),
  flashIds: new Set(),

  setProjectId: (id) => set({ projectId: id }),
  setTree: (tree) => set({ tree }),
  setChildren: (parentId, children) =>
    set((s) => ({ tree: applySetChildren(s.tree, parentId, children) })),

  toggleExpanded: (id) =>
    set((s) => {
      const next = new Set(s.expandedIds);
      if (next.has(id)) next.delete(id); else next.add(id);
      return { expandedIds: next };
    }),
  setExpanded: (id, expanded) =>
    set((s) => {
      const next = new Set(s.expandedIds);
      if (expanded) next.add(id); else next.delete(id);
      return { expandedIds: next };
    }),

  setLoading: (id, loading) =>
    set((s) => {
      const next = new Set(s.loadingIds);
      if (loading) next.add(id); else next.delete(id);
      return { loadingIds: next };
    }),

  setGitStatus: (gitStatus) => set({ gitStatus }),

  insertNode: (parentId, node) => {
    const { tree } = get();
    const result = applyInsert(tree, parentId, node);
    if (result.inserted) set({ tree: result.tree });
    return result.inserted;
  },

  removeNode: (id) => {
    const { tree } = get();
    const result = applyRemove(tree, id);
    if (result.removed) set({ tree: result.tree });
    return result.removed;
  },

  mergeFullCrawl: (crawl) =>
    set((s) => ({ tree: applyMergeCrawl(s.tree, crawl) })),

  flash: (id) => {
    set((s) => {
      if (s.flashIds.has(id)) return s;
      const next = new Set(s.flashIds);
      next.add(id);
      return { flashIds: next };
    });
    // Reset / replace any existing timer so re-flashes restart the duration.
    const prev = flashTimers.get(id);
    if (prev) clearTimeout(prev);
    flashTimers.set(
      id,
      setTimeout(() => {
        set((s) => {
          if (!s.flashIds.has(id)) return s;
          const next = new Set(s.flashIds);
          next.delete(id);
          return { flashIds: next };
        });
        flashTimers.delete(id);
      }, FLASH_DURATION_MS),
    );
  },

  reset: () => {
    flashTimers.forEach((t) => clearTimeout(t));
    flashTimers.clear();
    set({
      projectId: null,
      tree: [],
      expandedIds: new Set(),
      loadingIds: new Set(),
      gitStatus: new Map(),
      flashIds: new Set(),
    });
  },
}));
